import axios from 'axios';
import React, { useState, useEffect } from "react";

import AdminSideBar from './Admin-SideBar';
import PreviousAppointments from '../Doctor UI/PreviousAppointments';
import {  useLocation, Link } from 'react-router-dom';
import { API_DETAILS } from '../../App';
import Loader from '../../utils/loader';
import { formatDate } from '../../utils/formatDate';
import ProfileFields from '../ProfileFields';

const AdminViewPatientProfile = () => {

    const [prevAppts, setPrevAppts] = useState([]);
    const [upComingAppts, setUpcomingAppts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [providerInfo, setProviderInfo] = useState(null);
    // Separate from isLoading on purpose. When the page is opened from an
    // analytics drill-down it makes two requests -- the profile and the
    // appointments -- and either can finish first. isLoading tracks the
    // appointments; if it alone gated the render, the appointments arriving
    // first would render the profile panel with providerInfo still null and
    // throw on providerInfo.firstName: a white screen.
    const [profileFailed, setProfileFailed] = useState(false);
    const location = useLocation();


    useEffect(() => {
        // The record arrives in router state from the list page, so a refresh or a
        // pasted link has nothing to show. That used to throw and leave a blank
        // screen; now the page says so.
        if (!location.state) {
            setIsLoading(false);
            return;
        }

        // An analytics drill-down links here with only { userId }: it knows who
        // the patient is but not their whole record, and carrying a full record
        // through every list would mean the list endpoint returning more than it
        // needs. So when the name is missing, fetch the record by id. The patient
        // list still passes everything and takes the fast path below.
        //
        // /getUser/{userId} goes through assertSelfOrClinician, which admits an
        // admin and writes an audit row naming this patient — opening a chart from
        // a drill-down is recorded the same way as opening it from the list.
        if (location.state.userId && !location.state.firstName) {
            fetchProfileById(location.state.userId);
            fetchPrevAndUpcomingAppointments();
            return;
        }

        const temp = {
            userId: location.state.userId,
            firstName: location.state.firstName,
            lastName: location.state.lastName,
            email: location.state.email,
            phone1: location.state.phone1,
            address: location.state.address,
            city: location.state.city,
            createdAt: location.state.createdAt,
            zipCode: location.state.zipCode,
            specialty: location.state.specialty,
            status: location.state.status,
            userType: location.state.userType,
            state: location.state.state,
            profileImageURL: location.state.profileImageURL,
            license: location.state.license,
            username: location.state.username,
            gender: location.state.gender,
            dateOfBirth: location.state.dateOfBirth
        };
        setProviderInfo(temp);
        fetchPrevAndUpcomingAppointments();
    }, []);


    const fetchProfileById = async (userId) => {
        const url = API_DETAILS.baseUrl + API_DETAILS.PORT + API_DETAILS.baseExtension + `/getUser/${userId}`;
        try {
            const res = await axios.get(url);
            const user = res.data || {};
            setProviderInfo({
                userId: user.userId,
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                phone1: user.phone1,
                address: user.address,
                city: user.city,
                state: user.state,
                zipCode: user.zipCode,
                gender: user.gender,
                dateOfBirth: user.dateOfBirth,
                createdAt: user.createdAt,
                userType: user.userType,
            });
        } catch (err) {
            // Say the patient could not be loaded rather than rendering a
            // half-filled profile, which would look complete and is not.
            setProviderInfo(null);
            setProfileFailed(true);
        }
    };

    const fetchPrevAndUpcomingAppointments = async () => {
        const url = API_DETAILS.baseUrl + API_DETAILS.PORT + API_DETAILS.baseExtension + `/appointments/${location.state.userId}?timeZone=${Intl.DateTimeFormat().resolvedOptions().timeZone}`;
        const config = {
            headers: {
                'Access-Control-Allow-Origin': '*',
                'Access-Control-Allow-Methods': '*',
                'Content-Type': 'application/json'
            }
        };
        try {
            const res = await axios.get(url, config);
            setPrevAppts(res.data.previousAppointments);
            setUpcomingAppts(res.data.upComingAppointments);
            setIsLoading(false);
        }
        catch (err) {
            setIsLoading(false);
        }
    }


    // Renders the appointments it is given. It used to copy them into its own
    // state on mount — but being declared inside the page component it was
    // remounted on every render, and as a nested component with an empty
    // dependency list it also never picked up appointments that arrived later.
    const TodayAppointmentList = () => {
        return (
            <div>
                {(upComingAppts || []).map((item) => (
                    <div className='previous-appointment' key={item.id}>
                        <div>
                            <h3 >{String(item.time || '').split(',')[0]}, {item.patientstime} - {item.name}</h3>
                        </div>
                        <ul>
                            <li>Date : {item.time}</li>
                            <li>Time : {item.patientstime}</li>
                            <li>Diagnosis: {item.diagnosisname}</li>
                            <li>Symptom score: {item.symptomscore}</li>
                            <li>Meet-type: {item.meetup}</li>
                            {/* <p>{item.meetup}</p> */}
                        </ul>
                    </div>
                ))}
            </div>
        );
    };


    if (!location.state || profileFailed) {
        return (
            <div>
                <div className='hm'>
                    <div className='sidebar'>
                        <AdminSideBar data={'admin-patients'} />
                    </div>
                    <div className="admin-view-doctor-container" style={{ width: '100%', padding: '24px' }}>
                        <h2>We couldn't load this patient</h2>
                        <p>Open a patient from the list to see their profile.</p>
                        <Link to="/admin-patients">Back to patients</Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className='hm'>
                <div className='sidebar'>
                    <AdminSideBar data={'admin-patients'} />
                </div>
                <div className="admin-view-doctor-container" style={{ width: '100%' }}>
                    <div className='admin-view-doctor-header'>
                        <div className='header'>
                            <h1 style={{ marginLeft: '15px' }}>Youro</h1>
                        </div>
                    </div>
                    <div className='admin-view-doctor-body'>
                        {/* Ready only once BOTH requests are in. See profileFailed. */}
                        <Loader active={isLoading || !providerInfo} />
                        {
                            !isLoading && providerInfo && <>
                                <div className='all-plans-patient'>
                                    <div className='admin-details-view'>
                                        <h2>Upcoming Appointments</h2>
                                        {TodayAppointmentList()}</div>
                                    <div className='admin-details-view'>
                                        <h2>Previous Appointments</h2>
                                        <PreviousAppointments data={prevAppts} />
                                    </div>
                                    <div className='admin-details-view'>

                                        <img style={{ margin: '15px auto 30px auto', borderRadius: '5px', display: 'block' }} src={ require("../../assets/Display_icon.png") } alt="Preview" width="150" height="150" />
                                        {/* Labels and values used to be two parallel columns of <p>,
                                            aligned only by both lists producing the same line heights.
                                            They stopped matching, so a patient's date of birth appeared
                                            captioned "License Number". ProfileFields puts each label in
                                            the same grid row as its own value, so they cannot drift.

                                            License Number is gone: only providers have one, set through
                                            registerStaff. On a patient it was always going to be blank,
                                            and a blank row is what let the misalignment hide. */}
                                        <ProfileFields
                                            fields={[
                                                { label: 'First Name', value: providerInfo.firstName },
                                                { label: 'Last Name', value: providerInfo.lastName },
                                                { label: 'Email', value: providerInfo.email },
                                                { label: 'Mobile Number', value: providerInfo.phone1 },
                                                { label: 'Gender', value: providerInfo.gender },
                                                { label: 'Date of Birth', value: formatDate(providerInfo.dateOfBirth) },
                                                {
                                                    label: 'Address',
                                                    value: [providerInfo.address, providerInfo.city,
                                                        [providerInfo.state, providerInfo.zipCode].filter(Boolean).join(' ')]
                                                        .filter(Boolean).join(', '),
                                                },
                                                {
                                                    // Accounts older than the created_at column have no
                                                    // date to show, and ProfileFields says so rather than
                                                    // leaving a gap.
                                                    label: 'Signed Up',
                                                    value: providerInfo.createdAt
                                                        ? new Date(providerInfo.createdAt).toLocaleString('en-US', {
                                                            year: 'numeric', month: 'short', day: 'numeric',
                                                            hour: 'numeric', minute: '2-digit',
                                                        })
                                                        : null,
                                                },
                                            ]}
                                        />
                                    </div>
                                </div>
                            </>
                        }
                    </div>
                </div>
            </div>

        </div>
    );

};

export default AdminViewPatientProfile;
